import React, { useState, useEffect } from 'react'
import { faArrowRight, faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { selectSkillName, selectChallenge, selectTargetRole, selectExperienceLevel } from '../../../redux/skillSlice'
import { saveUserInfo, selectUserInfoSaving, selectUserInfoError, setUserInfo, selectUserInfo } from '../../../redux/userInfoSlice'
import { countryPhoneCodes } from '../../../utilities/countryPhoneCodes'
import { Listbox, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import useWindowSize from '../../../hooks/useWindowSize'
import { toast } from 'react-toastify'
import { selectInitSessionUserDetails, selectIsEvenUser } from '../../../redux/onboardingUserDetails'
import { selectIsAnalysisPageVisited, selectIsPricingPageVisited } from '../../../redux/newAnalysisSlice'
import { Mixpanel } from '../../../config/Mixpanel'
import constants from '../../../utilities/constants'

// Custom CountryCode component for UserDetails styling
const CustomCountryCode = ({ selected, setSelected }) => {
  return (
    <Listbox value={selected} onChange={setSelected}>
      {({ open }) => (
        <>
          <div className="relative">
            <Listbox.Button className={`relative  bg-[#406AFF]/15 w-[70px] h-[49px] pl-2 pr-4 rounded-[8px] border-1 border-[#70AEFF]/35 focus:ring-0 focus:outline-[#7FC241]`}>
              <div>
                <p className='text-white text-sm'>{selected.dial_code}</p>
                <FontAwesomeIcon icon={faCaretDown} className='text-white/60 text-sm absolute top-4 right-2' aria-hidden="true" />
              </div>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute max-h-[170px] w-[300%] pl-2 mt-2 rounded-[8px] pt-2 overflow-y-scroll bg-[#406AFF]/90 backdrop-blur-sm border border-[#70AEFF]/35 z-50">
                {countryPhoneCodes.map((item) => (
                  <Listbox.Option
                    key={item.code}
                    className='pb-1'
                    value={item}
                  >
                    {({ selected, active }) => (
                      <>
                        <div className={`bg-transparent flex items-center gap-3 justify-start text-sm cursor-pointer hover:bg-[#406AFF]/20 p-2 rounded text-white ${active ? 'bg-[#406AFF]/30' : ''}`}>
                          <p className='mr-2 w-[20%]'>{`${item.dial_code}`}</p>
                          <p className='w-[80%] text-clip'>{`${item.name}`}</p>
                        </div>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  )
}

const UserDetails = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Get data from Redux
  const { width } = useWindowSize()
  const skillName = useSelector(selectSkillName) || 'Product Management'
  const challenge = useSelector(selectChallenge)
  const targetRole = useSelector(selectTargetRole)
  const experienceLevel = useSelector(selectExperienceLevel)
  const userSessionDetails = useSelector(selectInitSessionUserDetails)
  const savedUserInfo = useSelector(selectUserInfo)
  const evenUser = useSelector(selectIsEvenUser);
  // Local state for user details form
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    phone: ''
  });

  const isAnalysisPageVisited = useSelector(selectIsAnalysisPageVisited)
  const isPricingPageVisited = useSelector(selectIsPricingPageVisited)
  // WhatsApp number state (following Register component pattern)
  const [selectedCountry, setSelectedCountry] = useState(countryPhoneCodes.find(country => country.dial_code === '+91'))

  // Track page load
  useEffect(() => {
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_USER_DETAILS_PAGE_LOADED, {
      skill: skillName,
      challenge: challenge,
      targetRole: targetRole,
      experienceLevel: experienceLevel,
      session_id: userSessionDetails?.session_id
    })
  }, [skillName, challenge, targetRole, experienceLevel, userSessionDetails?.session_id])

  // Initialize form with saved user info when component mounts

  useEffect(() => {
    if (isAnalysisPageVisited) {
      navigate('/onboarding/analysis')
    }
    else if (isPricingPageVisited) {
      navigate('/onboarding/pricing')
    }
  }, [isAnalysisPageVisited, isPricingPageVisited, navigate])

  useEffect(() => {
    if (savedUserInfo) {
      console.log('UserDetails - Initializing from saved info:', savedUserInfo)

      // Extract name
      if (savedUserInfo.name) {
        setFormData(prev => ({
          ...prev,
          firstName: savedUserInfo.name
        }))
      }

      // Extract email
      if (savedUserInfo.email) {
        setFormData(prev => ({
          ...prev,
          email: savedUserInfo.email
        }))
      }

      // Extract phone and country code
      if (savedUserInfo.phone) {
        // Try to extract country code and phone number
        const phoneStr = savedUserInfo.phone

        // Find matching country code
        const matchingCountry = countryPhoneCodes.find(country =>
          phoneStr.startsWith(country.dial_code)
        )

        if (matchingCountry) {
          setSelectedCountry(matchingCountry)
          // Extract phone number without country code
          const phoneNumber = phoneStr.replace(matchingCountry.dial_code, '')
          setFormData(prev => ({
            ...prev,
            phone: phoneNumber
          }))
        } else {
          // If no matching country code found, treat entire string as phone number
          setFormData(prev => ({
            ...prev,
            phone: phoneStr
          }))
        }
      }
    }
  }, [savedUserInfo])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Track form field focus
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_FORM_FIELD_FOCUSED, {
      field: field,
      session_id: userSessionDetails?.session_id
    })
  }

  const handlePhoneChange = (e) => {
    setFormData(prev => ({
      ...prev,
      phone: e.target.value
    }))
  }

  const handleCountryChange = (country) => {
    setSelectedCountry(country)
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_COUNTRY_CODE_CHANGED, {
      country_code: country.dial_code,
      country_name: country.name,
      session_id: userSessionDetails?.session_id
    })
  }

  const handleSubmit = async () => {
    //check email regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address')
      return
    }

    // Combine phone data like in Register component
    const completeFormData = {
      ...formData,
      countryCode: selectedCountry.dial_code,
      fullPhoneNumber: selectedCountry.dial_code + formData.phone
    }

    // Here you can save user details to Redux or API
    console.log('User Details:', completeFormData)
    console.log('Complete Onboarding Data:', {
      skillName,
      challenge,
      targetRole,
      experienceLevel,
      userDetails: completeFormData
    })

    if (!userSessionDetails?.session_id) {
      toast.error('Session not found. Please refresh and try again.')
      return
    }

    // Mixpanel identify and people.set
    const userId = userSessionDetails.session_id || completeFormData.email

    Mixpanel.people.set({
      $email: completeFormData?.email,
      $name: completeFormData?.firstName,
      $phone: completeFormData?.fullPhoneNumber,
      skill: skillName,
      session_id: userSessionDetails?.session_id
    })

    // Track form submission
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_USER_DETAILS_FORM_SUBMITTED, {
      name: completeFormData.firstName,
      email: completeFormData.email,
      phone: completeFormData.fullPhoneNumber,
      skill: skillName,
      challenge: challenge,
      targetRole: targetRole,
      experienceLevel: experienceLevel,
      session_id: userSessionDetails?.session_id
    })

    // Store user info in Redux without API call
    dispatch(setUserInfo({
      session_id: userSessionDetails.session_id,
      name: completeFormData.firstName,
      email: completeFormData.email,
      phone: completeFormData.fullPhoneNumber,
      uuid: userSessionDetails.session_id // or use actual uuid if available
    }))

    if (experienceLevel === "beginner") {
      navigate('/onboarding/loader-4')
      return;
    }
    // Navigate to loader page where API call will be made
    navigate('/onboarding/loader-3')
  }

  const isFormValid = formData.firstName && formData.email && formData.phone

  return (
    <div className='flex-1 flex flex-col mt-8 w-full md:max-w-[45rem] mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative'>


      {/* Main content */}
      <div className='flex flex-col p-[2px] rounded-[1.5rem]' style={{
        backgroundImage: `linear-gradient(
          to right,
          rgba(64, 106, 255, 0.3) 0%,
          rgba(64, 106, 255, 1) 56%,
          rgba(64, 106, 255, 0.3) 100%
        )`
      }}>
        {/* Main content card */}
        <div className='flex flex-col p-6 rounded-[1.5rem]  shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] w-full relative overflow-hidden' style={{ backgroundImage: `radial-gradient(circle, #0B305F 0%, #0A1E3F 100%)` }}>

          <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px] pointer-events-none"
            style={{ backgroundImage: `url('https://img.learntube.ai/public-assets/personalise/web.webp')` }} />
          <div className='w-full rounded-[2rem] shadow-lg'>
            {/* Title */}
            <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2]'>
              Where should we send your personalised course?
            </h1>
            {/* <p className='mt-2 text-[0.875rem] text-white/50 leading-[1.4]'>
              Enter your details to save and access your progress anytime.
            </p> */}

            {/* Show saved onboarding details */}
            {/* Form Fields */}

            <div className='mt-8 space-y-6 mb-8 w-full  '>
              {/* First input field with green border (focused state) */}
              <div>
                <label htmlFor='firstName' className='text-white/50 text-[12px] mb-2 block'>NAME</label>
                <input
                  type='text'
                  id='firstName'
                  name='firstName'
                  autoComplete='given-name'
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder='First Name'
                  className='w-full p-4 h-[49px] bg-[#406AFF]/15 placeholder:text-white/60 placeholder:text-[14px] rounded-[8px] text-white border border-[#70AEFF]/35 focus:border-[#7FC241] focus:outline-none focus:ring-0'
                />
              </div>

              {/* Second input field */}
              <div>
                <label htmlFor='email' className='text-white/50 text-[12px] mb-2 block'>EMAIL</label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  autoComplete='email'
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder='Email Address'
                  className='w-full p-4 h-[49px] bg-[#406AFF]/15 placeholder:text-white/60 placeholder:text-[14px] rounded-[8px] text-white border border-[#70AEFF]/35 focus:border-[#7FC241] focus:outline-none focus:ring-0'
                />
              </div>

              {/* WhatsApp Number Row - Following Register component pattern */}
              <div className="w-full">
                <label htmlFor='phoneNo' className='text-white/50 text-[12px] mb-2 block'>WHATSAPP</label>
                <div className='flex gap-3'>
                  <CustomCountryCode selected={selectedCountry} setSelected={handleCountryChange} />
                  <input
                    type="tel"
                    id='phoneNo'
                    name='phone'
                    autoComplete='tel'
                    className='w-full p-4 h-[49px] bg-[#406AFF]/15 placeholder:text-white/60 placeholder:text-[14px] rounded-[8px] text-white border border-[#70AEFF]/35 focus:border-[#7FC241] focus:outline-none focus:ring-0'
                    placeholder='Enter your phone number'
                    value={formData.phone}
                    onChange={handlePhoneChange}
                  />
                </div>
              </div>

              <style jsx>{`
                .country-code-wrapper .bg-[#2D4F71] {
                  background: rgba(64, 106, 255, 0.15) !important;
                  border: 1px solid rgba(112, 174, 255, 0.35) !important;
                  border-radius: 8px !important;
                  height: 49px !important;
                }
                
                /* Autocomplete background styling */
                input:-webkit-autofill,
                input:-webkit-autofill:hover,
                input:-webkit-autofill:focus,
                input:-webkit-autofill:active {
                  -webkit-box-shadow: 0 0 0 30px #133772 inset !important;
                  -webkit-text-fill-color: white !important;
                  box-shadow: 0 0 0 30px #133772 inset !important;
                }
              `}</style>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!isFormValid}
              className={`w-full flex items-center justify-center gap-2 p-4 rounded-xl font-semibold text-black ${width <= 360 ? 'text-[16px]' : 'text-[18px]'} transition-all duration-200 ${isFormValid
                ? 'bg-[#7FC241] hover:bg-[#8bd845] shadow-lg'
                : 'bg-[#7FC241]/50 cursor-not-allowed'
                }`}
            >
              <span>Continue To Skill Analysis</span>
              <FontAwesomeIcon icon={faArrowRight} className='text-sm' />
            </button>
            <p className='mt-3 text-white/50 italic text-[12px] text-center leading-[130%]'>🔐Your info. is safe and used only to personalise course.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserDetails
